import React, { useContext, useEffect } from 'react'
import { Box, Button, CircularProgress } from '@mui/material'
import { useQuery } from '@tanstack/react-query'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { ensureSafelineCloud } from '@/api'
import AppLoader from '@/layout/AppLoader'
import { AuthComponent, AuthContext } from '@/layout/Auth'

const SafelineEntryContent = () => {
  const { app_key = '' } = useParams()
  const location = useLocation()
  const navigate = useNavigate()
  const { authInfo } = useContext(AuthContext)
  const orgIdFromUrl = /^safeline-cloud-(\d+)$/.exec(app_key)?.[1]
  const orgId = orgIdFromUrl ? Number(orgIdFromUrl) : undefined

  useEffect(() => {
    if (authInfo.loading || authInfo.loadingOrg || !authInfo.auth) {
      return
    }

    const pathAfterKey = location.pathname.slice(`/console/app/${app_key}`.length)
    if (!authInfo.hasOrg) {
      const target = `/console/app/safeline-cloud${pathAfterKey}${location.search}${location.hash}`
      navigate(`/console/starter?redirect=${encodeURIComponent(target)}`, { replace: true })
    } else if (!orgIdFromUrl && authInfo.orgInfo?.id) {
      navigate(
        {
          pathname: `/console/app/safeline-cloud-${authInfo.orgInfo.id}${pathAfterKey}`,
          search: location.search,
          hash: location.hash,
        },
        { replace: true },
      )
    }
  }, [
    app_key,
    authInfo.auth,
    authInfo.hasOrg,
    authInfo.loading,
    authInfo.loadingOrg,
    authInfo.orgInfo?.id,
    location,
    navigate,
    orgIdFromUrl,
  ])

  const { isSuccess, isError, isFetching, isFetchedAfterMount, error, refetch } = useQuery(
    ['ensureSafelineCloud', authInfo.userInfo?.id, orgId],
    () => ensureSafelineCloud(orgId!),
    {
      enabled:
        !authInfo.loading &&
        !authInfo.loadingOrg &&
        authInfo.auth &&
        authInfo.hasOrg &&
        !!authInfo.userInfo?.id &&
        !!orgId,
      refetchOnMount: 'always',
      refetchOnWindowFocus: false,
      retry: false,
    },
  )

  if (
    !authInfo.loading &&
    !authInfo.loadingOrg &&
    authInfo.auth &&
    authInfo.hasOrg &&
    isSuccess &&
    isFetchedAfterMount &&
    !isFetching
  ) {
    return <AppLoader />
  }

  if (orgIdFromUrl && !orgId) {
    return <Box sx={{ p: 4 }}>空间地址无效，请重新从雷池官网进入。</Box>
  }

  if (!authInfo.loading && !authInfo.auth) {
    const target = `${location.pathname}${location.search}${location.hash}`
    return (
      <Box sx={{ p: 4 }}>
        <Box sx={{ mb: 2 }}>请先登录后进入雷池授权商店。</Box>
        <Button variant='contained' href={`/login?redirect=${encodeURIComponent(target)}`}>
          去登录
        </Button>
      </Box>
    )
  }

  if (isError) {
    const responseError = error as { response?: { data?: { message?: string } } }
    const message = typeof error === 'string' ? error : responseError?.response?.data?.message || '请稍后重试'
    return (
      <Box sx={{ p: 4 }}>
        <Box sx={{ mb: 2 }}>雷池授权商店开通或授权失败：{message}</Box>
        <Button variant='contained' disabled={isFetching} onClick={() => refetch()}>
          重试
        </Button>
      </Box>
    )
  }

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 4 }}>
      <CircularProgress size={20} />
      正在准备雷池授权商店…
    </Box>
  )
}

const SafelineEntry = () => (
  <AuthComponent>
    <SafelineEntryContent />
  </AuthComponent>
)

export default SafelineEntry
